import { faArrowUpRightFromSquare, faBedPulse, faCalendarDays, faCaretDown, faCaretRight, faChartLine, faCheckCircle, faCircle, faClose, faExclamationCircle, faHeartPulse, faMinus, faQuestionCircle, faRocket, faSpinner, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import useResizeObserver from "@react-hook/resize-observer";
import {
    BarElement,
    CategoryScale,
    Chart as ChartJS,
    Tooltip as ChartJSTooltip,
    Filler,
    Legend,
    LineElement,
    LinearScale,
    PointElement,
    TimeScale,
    Title,
} from 'chart.js';
import 'chartjs-adapter-moment';
import { CRS } from 'leaflet';
import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, ButtonGroup, Card, Col, Container, OverlayTrigger, Row, Table, Tooltip } from "react-bootstrap";
import { MapContainer } from 'react-leaflet';
import { useLocation } from 'react-router-dom';
import UplotReact from "uplot-react";

import * as hdf5 from 'jsfive';
import queryString from 'query-string';
import { Line } from 'react-chartjs-2';
import { callFhirMedications } from "../../amplify/api-gateway/fetch-sync-fhir";
import { ACTIVE_PATIENT_VISITS_AT, ADMIT_RISK_SCORES_FOR_ACTIVE_PATIENTS, ANNOTATIONS_BY_CSN, ANNOTATIONS_EXTENSIONS_BY_CSN, ARCHIVED_DATA, ARCHIVED_WAVEFORMS, Annotation, LABS_BY_CSN, Lab, MEDICATIONS_BY_CSN, METRICS_TRENDS, Medication, NUMERICS_BY_CSN, PATIENT_VISIT_BY_INTERNAL_CSN, RadiologyReport, callQuery } from "../../amplify/api-gateway/query";
import { PatientVisit } from "../../amplify/api-gateway/search-patients";
import { AppAuthStateProps } from "../../types/propTypes";
import { debounce, generateTimestamps, isLastVisibleNumericsPlot, off, on, verticallySeparatePoints } from "../../utils/plot";
import { getCurrMinMax, roundNumberIfAppropriate } from "../../utils/scales";
import { formatUserReadableDate, getDatePath, getEarliestTime, getHourMinuteDifference, getLatestTime, getLocalDateStrFromDate, getLocalTimeStrFromDate, getMinuteTruncatedEpoch, getRelativeDate, getSecondDateStrFromDateStr, getSqlDateStrFromDateStr } from "../../utils/time";
import { convertRadiologyReportToAnnotation, parseDataForTimelinePlot, parseLabsDataForModal, parseUniqueECGClassifications, parseWaveformAnnotations } from "../dashboard/DashboardDataParser";
import { PREFERRED_WAVEFORMS_ORDER, SelectedTimelineMinMax, convertToTempUnit, getAvailableNumerics, getAvailableWaveforms, getNumericOpts, getTimelineOpts, getWaveformAvailabilityOpts, getWaveformOpts } from "../dashboard/DashboardOpts";
import { LabsModal } from "../dashboard/Labs";
import { MedsModal } from "../dashboard/Meds";
import { OtherAnnotationModal } from "../dashboard/OtherAnnotationModal";
import TimelineContextMenu from "../dashboard/TimelineContextMenu";
import UserAnnotationModal from "../dashboard/UserAnnotationModal";
import RasterMap from "./RasterMap";

import annotationPlugin from 'chartjs-plugin-annotation';
import { readBinaryFile } from "../../amplify/s3/storage";
import { isDemoUser } from "../../utils/demo";
import useSetPagePatientVisit from "../common/GlobalOptionsContext";
import PersonalIdentifier from "../common/PersonalIdentifier";
import { OrdersModal } from "../dashboard/Orders";
import { CustomDateSelector } from "../filters/CustomDateSelector";
import { HIGHLIGHT_MAP_OPTIONS } from "../filters/DashboardFilterOptions";
import { SingleSelectMenu } from "../filters/SingleSelectMenu";
import { WaveformAnnotationSingleSelectMenu } from "../filters/WaveformAnnotationSingleSelectMenu";
import { METRIC_NAME_TO_DESCRIPTION, METRIC_NAME_TO_DISPLAY, MetricName, isMetricCount } from "../filters/metricsFilterOptions";
import ConfirmationModal from "./ConfirmationModal";
import { Insights } from "./Insights";
import "./Ops.scss";
import { getMappedDimensionValue, getMetricsLineOpts, metricColors } from "./metricsPlotOpts";
import { zoneToImageSize } from "./roomCoordinates";

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    TimeScale,
    Filler,
    Title,
    ChartJSTooltip,
    Legend,
    PointElement,
    LineElement,
    // plugin,
    annotationPlugin,
);

export const Ops = (props: AppAuthStateProps) => {

    const location = useLocation();
    const queryModel = queryString.parse(location.search);

    const OPS_NUMERICS_FILTER = {
        "numerics": ["HR", "NBP", "SpO2", "RR", "Temp", "HRV", "Perf"]
    };

    // This is the date where we are storing all of the demo patients
    const DEMO_MAGIC_DATE = new Date(1267466400000);

    const [internalCSN, setInternalCSN] = useState<string>();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [allPatients, setAllPatients] = useState<PatientVisit[]>([]);
    const [selectedZone, setSelectedZone] = useState<string>(isDemoUser(props.user) ? "ALPHA" : "ALL");
    const [selectedRoom, setSelectedRoom] = useState<string>("");
    const [isHistoricalView, setIsHistoricalView] = useState<boolean>(queryModel["date"] ? true : false);
    
    const [patientVisit, setPatientVisit] = useState<PatientVisit>();
    useSetPagePatientVisit(patientVisit);

    const patientVisitRef = useRef<PatientVisit>();
    useEffect(() => {
        patientVisitRef.current = patientVisit;
    }, [patientVisit]);

    const [featureImportance, setFeatureImportance] = useState<any>({});
    const [timestamps, setTimestamps] = useState<any>({});

    const [filterDateSelectorShow, setFilterDateSelectorShow] = useState<boolean>(false);
    const [filterDateOptions, setFilterDateOptions]: any = useState<any>({
        absoluteStart: queryModel["date"] ? new Date(Number(queryModel["date"])) : new Date(),
    })

    const [overviewData, setOverviewData] = useState<any>({});
    const [numericsData, setNumericsData] = useState<any>({});
    const [numericsRefRangeData, setNumericsRefRangeData] = useState<any>({});
    const [numericsHidden, setNumericsHidden] = useState<any>({});
    const numericsHiddenRef = useRef<any>();
    const [labsData, setLabsData] = useState<Lab[][]>([]);
    const [medsData, setMedsData] = useState<Medication[]>([]);
    const [tempUnit, setTempUnit] = useState<string>('C');
    const [lockTimelineZoom, setLockTimelineZoom] = useState<boolean>(false);

    const [filterWaveformOptions, setFilterWaveformOptions]: any = useState<any>({
        "waveformAnnotation": 0,
    });

    const [filterWaveformAnnotationShow, setFilterWaveformAnnotationShow] = useState<any>(false);

    const [waveformAvailabilityData, setWaveformAvailabilityData] = useState<any>([[], [], [], [], []]);
    const [waveformNumericsData, setWaveformNumericsData] = useState<any>({});
    const [waveformsData, setWaveformsData] = useState<any>({});

    const [errorMessage, setErrorMessage] = useState<string>("");
    const [isLoadingOverview, setIsLoadingOverview] = useState<any>(false);
    const [isLoadingNumerics, setIsLoadingNumerics] = useState<any>(false);
    const [isLoadingAnnotations, setIsLoadingAnnotations] = useState<boolean>(false);
    const [isLoadingWaveforms, setIsLoadingWaveforms] = useState<any>({});

    const [waveformAvailabilityOpts, setWaveformAvailabilityOpts] = useState<any>(null);
    const [waveformOpts, setWaveformOpts] = useState<any>([]);
    const [uniqueECGClassifications, setUniqueECGClassifications] = useState<any>([]);
    const [timelineOpts, setTimelineOpts] = useState<any>(null);
    const [numericOpts, setNumericOpts] = useState<any>([]);
    const [waveformNumericsOpts, setWaveformNumericOpts] = useState<any>([]);

    // Shows the custom annotation modal popup
    const [showContextMenu, setShowContextMenu] = useState<any>(false);
    const [contextMenuPos, setContextMenuPos] = useState<any>({ x: 0, y: 0 });
    const [showUserAnnotationModal, setShowUserAnnotationModal] = useState<any>(false);
    const [userAnnotationHighlightedTime, setUserAnnotationHighlightedTime] = useState<any>(null);
    const [userAnnotations, setUserAnnotations] = useState<any>([]);

    const [showOtherAnnotationsModal, setShowOtherAnnotationsModal] = useState<any>(false);
    const [selectedOtherAnnotations, setSelectedOtherAnnotations] = useState<Annotation[] | null>(null);
    const [otherAnnotations, setOtherAnnotations] = useState<any>([]);

    // Shows the labs modal popup
    const [showLabsModal, setShowLabsModal] = useState<any>(false);
    // If set, highlights the labs corresponding to this particular time in the labs modal
    const [labsModalHighlightedTime, setLabsModalHighlightedTime] = useState<any>(null);
    const [showMedsModal, setShowMedsModal] = useState<any>(false);
    const [showOrdersModal, setShowOrdersModal] = useState<any>(false);

    const [isLoadingMetrics, setIsLoadingMetrics] = useState<boolean>(false);
    const [dimensionValues, setDimensionValues] = useState<string[]>([]);

    const [selectedMode, setSelectedMode] = useState<string>("Metrics");
    const [selectedMapOrTable, setSelectedMapOrTable] = useState<string>("Map");

    const [metricNames, setMetricNames] = useState<string[]>([MetricName.ActivePatients, MetricName.ActiveLOS, MetricName.ActiveBoardingTime, MetricName.ActiveInRoomTime, MetricName.ActiveWaiting]);
    const [metricNameToData, setMetricNameToData] = useState<any>();
    const [metricNameToOps, setMetricNameToOps] = useState<any>();

    const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false);
    const [confirmationTime, setConfirmationTime] = useState<Date>(new Date());

    const [highlightedEvents, setHighlightedEvents] = useState<any>([]);
    const [selectedTimelineMinMax, setSelectedTimelineMinMax] = useState<SelectedTimelineMinMax>({});

    const target: any = React.useRef(null)
    const timelinePlotRef: any = React.useRef(null)
    const [timelinePlotContainerHeight, setTimelinePlotContainerHeight] = React.useState(180);
    const timelinePlotContainerRef: any = React.createRef();

    const chartRef = useRef<any>(null);
    const [hoverPosition, setHoverPosition] = useState<Date>();
    const [plotWidth, setPlotWidth] = React.useState(1024);
    const plotWidthRef = useRef(plotWidth);

    const doc = document;

    let x0: any;
    let lft0: any;
    let wid0: any;

    const lftWid = { left: null, width: null };

    // Run on the initial load to size the graphs properly
    //
    React.useLayoutEffect(() => {
        console.log("useLayoutEffect target", target);
        if (target.current) {
            const width = target.current.getBoundingClientRect().width / 3; // Takes into account how many window panes the screen is divided into
            console.log("useLayoutEffect initial load", width);
            setPlotWidth(width);

            if (patientVisit) {
                console.log("Setting width: useLayoutEffect", width);
                setTimelineOpts((prevOpt: any) => getTimelineOpts(addAnnotationToPlots, handleShowLabsModal, setShowUserAnnotationModal, handleShowOtherAnnotation, handleShowTimelineContextMenu, width, timelinePlotContainerHeight, patientVisit, setSelectedTimelineMinMax, lockTimelineZoom, props.tenant));
                setWaveformAvailabilityOpts(getWaveformAvailabilityOpts(addAnnotationToPlots, handleShowTimelineContextMenu, width, patientVisit));

                let minMax = getCurrMinMax(patientVisit, props.tenant);
                setNumericOpts((prevNumerics: any) => {
                    const numerics = getAvailableNumerics(patientVisit, OPS_NUMERICS_FILTER);
                    return numerics.map((numeric: any, idx: number) => getNumericOpts(plotWidthRef, numeric, idx, handleShowTimelineContextMenu, width, minMax, updateWaveformPreview, patientVisit,
                        isLastVisibleNumericsPlot(numerics, numeric, numericsHiddenRef)))
                });
            }
        }
    }, [target])

    useEffect(() => {
        plotWidthRef.current = plotWidth;
    }, [plotWidth]);


    useEffect(() => {
        if (metricNameToOps && Object.keys(metricNameToOps).length > 0) {
            let localMetricNameToOps: any = {};
            for (let name of Object.keys(metricNameToOps)) {
                localMetricNameToOps[name] = {
                    ...metricNameToOps[name],
                    plugins: {
                        ...metricNameToOps[name].plugins,
                        annotation: {
                            annotations: {
                                line1: {
                                    type: 'line',
                                    xMin: hoverPosition,
                                    xMax: hoverPosition,
                                    borderColor: '#999',
                                    borderDash: [2, 2],
                                    borderWidth: 1,
                                },
                            }
                        },
                    }
                };
            }
            setMetricNameToOps(localMetricNameToOps);
        }
    }, [hoverPosition]);

    // Run on subsequent iterations page width changes
    //
    useResizeObserver(target, (entry: any) => {
        let resizeTo = (entry.contentRect.width / 3) - 30; // Takes into account how maby window panes the page is divided into
        triggerResize(resizeTo);
    })

    const triggerResize = (resizeTo: number) => {
        setPlotWidth(resizeTo)

        const width = resizeTo;
        refreshPlots(width);
    }

    const refreshPlots = (width: number, lockTimelineZoomOverride?: boolean) => {
        if (patientVisit) {
            console.log("Setting timeline width: refreshPlots", width);
            setTimelineOpts(getTimelineOpts(addAnnotationToPlots, handleShowLabsModal, setShowUserAnnotationModal, handleShowOtherAnnotation, handleShowTimelineContextMenu, width, timelinePlotContainerHeight, patientVisit, setSelectedTimelineMinMax, lockTimelineZoomOverride !== undefined ? lockTimelineZoomOverride : lockTimelineZoom, props.tenant));
            setWaveformAvailabilityOpts(getWaveformAvailabilityOpts(addAnnotationToPlots, handleShowTimelineContextMenu, width, patientVisit));
            let minMax = getCurrMinMax(patientVisit, props.tenant);
            console.log("Setting width: refreshPlots", width);
            setNumericOpts((prevNumerics: any) => {
                const numerics = getAvailableNumerics(patientVisit, OPS_NUMERICS_FILTER);
                return numerics.map((numeric: any, idx: number) => getNumericOpts(plotWidthRef, numeric, idx, handleShowTimelineContextMenu, width, minMax, updateWaveformPreview, patientVisit,
                    isLastVisibleNumericsPlot(numerics, numeric, numericsHiddenRef)))
            });
        }
    }


    useEffect(() => {
        setIsLoading(true);
        callQuery({
            operation: ACTIVE_PATIENT_VISITS_AT,
            start: isDemoUser(props.user) ? getSqlDateStrFromDateStr(DEMO_MAGIC_DATE) : getSqlDateStrFromDateStr(filterDateOptions.absoluteStart),
        })
        .then((patients: PatientVisit[]) => {
            patients.sort(function(a, b) {
                if (a.bed) {
                    const bedComparison = a.bed!.localeCompare(b.bed!);
                    if (bedComparison !== 0) {
                        return bedComparison;
                    }
                }

                return a.arrival_time.localeCompare(b.arrival_time);
            });

            setIsLoading(false);
            setAllPatients(patients);
        });

        refresh(metricNames);
    }, [filterDateOptions])

    useEffect(() => {
        if (internalCSN) {
            callQuery({
                operation: PATIENT_VISIT_BY_INTERNAL_CSN,
                internal_csn: internalCSN,
            })
            .then((patients: PatientVisit[]) => {
                console.log("callQuery returned for internalCSN: " + internalCSN + ", found " + patients.length + " patients")
                let currPatientVisit: PatientVisit | null = null;
                for (var patient of patients) {
                    if (patient.internal_csn === internalCSN) {
                        currPatientVisit = patient;
                    }
                }

                if (currPatientVisit) {
                    setSelectedMode("Patient");
                    setHighlightedEvents([]);
                    setPatientVisit(currPatientVisit);
                    fetchNumerics(currPatientVisit);
                    fetchWaveforms(currPatientVisit);
                    fetchChartAndAnnotations(currPatientVisit);
                    // if (!isFirstLoad) {
                    //     // We don't need to reset zoom on the first load, but will need to reset
                    //     // the x-scales of the plots if the patient changes.
                    //     resetZoom(currPatientVisit);
                    // }
                    // setIsFirstLoad(false);
                }
            });
        }
    }, [internalCSN]);

    useEffect(() => {
        refresh(metricNames);
    }, [selectedZone]);


    useEffect(() => {
        console.log("Waveform filters updated, rerunning query");
        if (patientVisit) {
            if (patientVisit.archived !== "SCHEMA_V1") {
                fetchAnnotations(patientVisit);
            }
        }
    }, [filterWaveformOptions]);

    const updateFilterWaveformOptions = (option: string, val: any) => {
        setFilterWaveformOptions({
            ...filterOptions,
            [option]: val,
        });
    }

    const fetchChartAndAnnotations = (currPatientVisit: PatientVisit) => {
        console.log("Calling fetchChartAndAnnotations...");
        setIsLoadingOverview(true);

        const labsPromise = callQuery({
            operation: LABS_BY_CSN,
            csn: currPatientVisit.csn,
        });
        let medsPromise;
        if (currPatientVisit.departure_time) {
            // If the patient has left already, then we rely on the medications 
            // stored in the database because the medications are synced once
            // the patient leaves (MAR is an expensive call). 
            medsPromise = callQuery({
                operation: MEDICATIONS_BY_CSN,
                csn: currPatientVisit.csn,
            });
        } else {
            // If the patient is still active, then we rely on the medications 
            // directly from FHIR.
            medsPromise = callFhirMedications({
                csn: currPatientVisit.csn,
                mrn: currPatientVisit.mrn,
                external_id: currPatientVisit.external_id!,
            });
        }

        setIsLoadingAnnotations(true);
        const annotationsExtensionsPromise = callQuery({
            operation: ANNOTATIONS_EXTENSIONS_BY_CSN,
            csn: currPatientVisit.csn,
        });

        // Load all of the data that feeds into the timeline chart
        Promise.all([labsPromise, medsPromise, annotationsExtensionsPromise]).then((values) => {
            const labs: Lab[] = values[0];
            const meds: Medication[] = values[1];
            const annotations: Annotation[] = values[2]["annotations"];
            const radiologyReports: RadiologyReport[] = values[2]["radiology_reports"];
            annotations.push(...convertRadiologyReportToAnnotation(radiologyReports));

            // Process the annotations
            const waveformAvailability = parseWaveformAnnotations(annotations, currPatientVisit, uniqueECGClassifications, filterWaveformOptions);
            setUserAnnotations([...annotations.filter(annotation => annotation.annotation_source === "USER_ANNOTATION")])
            setOtherAnnotations([...annotations.filter(annotation => annotation.annotation_source === "RADIOLOGY_REPORT")])
            console.log("setWaveformAvailabilityData1", waveformAvailability)
            setWaveformAvailabilityData(waveformAvailability);
            setIsLoadingAnnotations(false);

            // Process the labs and medications
            const plotData = parseDataForTimelinePlot(currPatientVisit, labs, meds, annotations);
            setIsLoadingOverview(false);
            setOverviewData(plotData);
            console.log("Setting timeline width: chartsAndAnnotations", plotWidth);
            setTimelineOpts(getTimelineOpts(addAnnotationToPlots, handleShowLabsModal, setShowUserAnnotationModal, handleShowOtherAnnotation, handleShowTimelineContextMenu, plotWidth, timelinePlotContainerHeight, currPatientVisit, setSelectedTimelineMinMax, lockTimelineZoom, props.tenant));

            setLabsData(parseLabsDataForModal(labs));
            setMedsData(meds);
        });
    }

    const fetchAnnotations = (currPatientVisit: PatientVisit) => {
        console.log("Calling fetchAnnotations...");
        setIsLoadingAnnotations(true);

        const annotationsPromise = callQuery({
            operation: ANNOTATIONS_BY_CSN,
            csn: currPatientVisit.csn,
        });

        Promise.all([annotationsPromise]).then((values) => {
            console.log("fetchAnnotations promise returned...");

            const annotations: Annotation[] = values[0];
            const waveformAvailability = parseWaveformAnnotations(annotations, currPatientVisit, uniqueECGClassifications, filterWaveformOptions);

            if (currPatientVisit.archived !== "SCHEMA_V1") {
                setWaveformAvailabilityData(waveformAvailability);
            }
            setIsLoadingAnnotations(false);

            const classifications = parseUniqueECGClassifications(annotations, filterWaveformOptions);
            setUniqueECGClassifications(classifications);
        });
    }


    const fetchNumerics = (currPatientVisit: PatientVisit) => {
        setIsLoadingNumerics(true);

        let numericsPromise;
        if (currPatientVisit.archived === "SCHEMA_V1") {
            numericsPromise = callQuery({
                operation: ARCHIVED_DATA,
                csn: currPatientVisit.csn,
                start: getSqlDateStrFromDateStr(getEarliestTime(currPatientVisit)),
                end: getSqlDateStrFromDateStr(getLatestTime(currPatientVisit, props.tenant)),
                period: "1m",
                statistic: "avg",
                modalities: getAvailableNumerics(currPatientVisit, OPS_NUMERICS_FILTER),
            });
        } else {
            numericsPromise = callQuery({
                operation: NUMERICS_BY_CSN,
                csn: currPatientVisit.csn,
                start: getSqlDateStrFromDateStr(getEarliestTime(currPatientVisit)),
                end: getSqlDateStrFromDateStr(getLatestTime(currPatientVisit, props.tenant)),
                period: "1m",
                statistic: "avg",
                modalities: getAvailableNumerics(currPatientVisit, OPS_NUMERICS_FILTER),
                data_granularity: currPatientVisit.granularity,
            });
        }

        const promises = [numericsPromise];

        Promise.all(promises).then((promiseOutput: any) => {

            const data = promiseOutput[0];
            let referenceRange: any = null;
            if (promises.length > 1) {
                referenceRange = promiseOutput[1][0];
            }
            if (currPatientVisit.archived === "SCHEMA_V1") {
                if (data && data["numerics"]) {
                    processNumericsResponse(currPatientVisit, data["numerics"], referenceRange);
                    const waveformAvailability = parseWaveformAnnotations(data["annotations"], currPatientVisit, uniqueECGClassifications, filterWaveformOptions);
                    setWaveformAvailabilityData(waveformAvailability);
                } else {
                    processNumericsResponse(currPatientVisit, {}, referenceRange);
                    const waveformAvailability = parseWaveformAnnotations([], currPatientVisit, uniqueECGClassifications, filterWaveformOptions);
                    setWaveformAvailabilityData(waveformAvailability);
                }
            } else {
                processNumericsResponse(currPatientVisit, data, referenceRange);
            }

        });
    }

    const processNumericsResponse = (currPatientVisit: PatientVisit, items: any, referenceRange?: any) => {
        const refRangeText: any = {};

        // Clear the zoomed range, which is a global variable that is set whenever there 
        // is an active annotation. This is required as adding annotations will inherently
        // cause the plot to redraw, which will reset the zoom.
        // @ts-ignore
        globalThis.zoomedRange = {};
        setSelectedTimelineMinMax({});

        for (let key of [...Object.keys(items)]) {
            // This ensures that the min/max bounds are respected when the zoom is reset
            items[key][0].unshift(getEarliestTime(currPatientVisit) / 1000);
            for (let i = 1; i < items[key].length; i++) {
                items[key][i].unshift(null);
            }

            items[key][0].push(getLatestTime(currPatientVisit, props.tenant) / 1000);
            for (let i = 1; i < items[key].length; i++) {
                items[key][i].push(null);
            }

            items[key].push(new Array(items[key][0].length).fill(null))
            items[key].push(new Array(items[key][0].length).fill(null))
            items[key].push(new Array(items[key][0].length).fill(null))
            items[key].push(new Array(items[key][0].length).fill(null))

            if (referenceRange) {

                if (key === "NBP") {
                    const nbpsLower = Math.round(referenceRange[`nbps_lower`] * 100) / 100;
                    const nbpsUpper = Math.round(referenceRange[`nbps_upper`] * 100) / 100;
                    const nbpdLower = Math.round(referenceRange[`nbpd_lower`] * 100) / 100;
                    const nbpdUpper = Math.round(referenceRange[`nbpd_upper`] * 100) / 100;

                    refRangeText[key] = `Systolic/Diastolic Reference Range: [${nbpsLower} - ${nbpsUpper}]/[${nbpdLower} - ${nbpdUpper}]`

                    for (let i = 1; i < 5; i += 2) {
                        items[key][i].forEach((element: any, k: number) => {
                            if (element && (element < nbpsLower || element > nbpsUpper)) {
                                items[key][i + 4][k] = element;
                            }
                        });
                    }
                    for (let i = 2; i < 5; i += 2) {
                        items[key][i].forEach((element: any, k: number) => {
                            if (element && (element < nbpdLower || element > nbpdUpper)) {
                                items[key][i + 4][k] = element;
                            }
                        });
                    }
                } else {
                    const lower = Math.round(referenceRange[`${key.toLowerCase()}_lower`] * 100) / 100;
                    const upper = Math.round(referenceRange[`${key.toLowerCase()}_upper`] * 100) / 100;

                    refRangeText[key] = `${key} Reference Range: [${lower} - ${upper}]`

                    for (let i = 1; i < 5; i++) {
                        items[key][i].forEach((element: any, k: number) => {
                            if (element && (element < lower || element > upper)) {
                                items[key][i + 4][k] = element;
                            }
                        });
                    }
                }
            }
        }
        setNumericsData(items);
        setNumericsRefRangeData(refRangeText);

        // Note we add a custom object to the window so that we can later access the numerics data 
        // when displaying the tooltips
        // @ts-ignore
        window.numericsData = items;

        let hiddenNumerics: any = {};
        getAvailableNumerics(currPatientVisit, OPS_NUMERICS_FILTER).forEach((numeric: string, idx: number) => {
            if (!items[numeric] || items[numeric][0].length === 0) {
                hiddenNumerics[numeric] = true;
            }
        });

        let minMax = getCurrMinMax(currPatientVisit, props.tenant);
        console.log("Setting width: fetchNumerics", plotWidth);
        setNumericOpts((prevNumerics: any) => {
            const numerics = getAvailableNumerics(currPatientVisit, OPS_NUMERICS_FILTER);
            return numerics.map((numeric: any, idx: number) => getNumericOpts(plotWidthRef, numeric, idx, handleShowTimelineContextMenu, null, minMax, updateWaveformPreview, currPatientVisit,
                isLastVisibleNumericsPlot(numerics, numeric, numericsHiddenRef)))
        });

        setIsLoadingNumerics(false);
    }

    const fetchWaveforms = (currPatientVisit: PatientVisit, targetDate?: Date) => {
        if (currPatientVisit) {
            const loading: any = [];
            for (let waveform of getAvailableWaveforms(currPatientVisit, filterOptions)) {
                loading[waveform] = true;
            }

            let date: Date;
            if (targetDate) {
                date = targetDate;
            } else {
                // The user has not specified a target date
                // This most likely is because this is the first time loading this patient.
                // Pick a random time between the admit time and the dispo time.
                const minTime = new Date(currPatientVisit.arrival_time).getTime();
                const maxTime = currPatientVisit.dispo_time ? new Date(currPatientVisit.dispo_time).getTime() : new Date().getTime();
                date = new Date(maxTime - 1000 * 60);
                // It is presumed if the target date was already set, then we don't need to move the annotation
                globalThis.xAnnotationMarksForNumerics = [
                    {
                        type: 'waveformMark',
                        from: date.getTime() / 1000,
                        to: date.getTime() / 1000 + 60,
                        label: 'Waveform',
                    }
                ];
                globalThis.xAnnotationMarksForWaveforms = [
                    {
                        type: 'waveformMark',
                        from: date.getTime() / 1000,
                        to: date.getTime() / 1000 + 60,
                        label: 'Waveform',
                    }
                ];
                setWaveformAvailabilityOpts(getWaveformAvailabilityOpts(addAnnotationToPlots, handleShowTimelineContextMenu, plotWidth, currPatientVisit));
            }

            setIsLoadingWaveforms(loading);
            const available_waveforms = getAvailableWaveforms(currPatientVisit, filterOptions);

            if (currPatientVisit.archived === "SCHEMA_V1") {
                callQuery({
                    operation: ARCHIVED_WAVEFORMS,
                    csn: currPatientVisit.csn,
                    start: date.toISOString(),
                })
                .then((data: any) => {
                    if (data && data["waveforms"]) {
                        let waveformsData: any = {};
                        for (let waveform of available_waveforms) {
                            let freq = 500;
                            if (waveform === "Pleth") {
                                freq = 125;
                            } else if (waveform === "Resp") {
                                freq = 62.5;
                            }
                            if (data["waveforms"][waveform]) {
                                const timestamps = generateTimestamps(date.getTime() / 1000, 60, freq);
                                const vals = data["waveforms"][waveform];

                                console.log(`Fetched waveforms ${waveform} of shape ${vals.length}`);

                                waveformsData[waveform] = [
                                    timestamps,
                                    vals
                                ];
                            }
                        }
                        setWaveformsData(waveformsData);
                        setWaveformOpts(available_waveforms.map((waveform: any) => getWaveformOpts(waveform, plotWidth)));
                    }

                    let loading: any = {};
                    for (let waveform of getAvailableWaveforms(currPatientVisit, filterOptions)) {
                        loading[waveform] = false;
                    }
                    setIsLoadingWaveforms(loading);
                });
            } else {
                for (let waveform of available_waveforms) {
                    readBinaryFile(`mrn=${currPatientVisit.internal_mrn}/csn=${currPatientVisit.internal_csn}/${getDatePath(date)}/${waveform}.h5`, props.tenant).then((buffer: any) => {
                        var f = new hdf5.File(buffer, "");

                        const timestamps = generateTimestamps(date.getTime() / 1000, 60, 125);
                        const vals = f.get(waveform).value;

                        console.log(`Fetched waveforms ${waveform} of shape ${vals.length}`);

                        setWaveformOpts(available_waveforms.map((waveform: any) => getWaveformOpts(waveform, plotWidth)));
                        setWaveformsData((prevState: any) => ({
                            ...prevState,
                            [waveform]: [
                                timestamps,
                                vals
                            ],
                        }));

                        // If we hide graph if all values are the same, we may accidentally confuse users
                        //
                        // if (!areValsAreSame(vals)) {
                        //     setWaveformOpts(available_waveforms.map((waveform: any) => getWaveformOpts(waveform, plotWidth)));
                        //     setWaveformsData((prevState: any) => ({
                        //         ...prevState,
                        //         [waveform]: [
                        //             timestamps,
                        //             vals
                        //         ],
                        //     }));
                        // } else {
                        //     setWaveformsData((prevState: any) => ({
                        //         ...prevState,
                        //         [waveform]: [],
                        //     }));
                        // }
                        setIsLoadingWaveforms((prevState: any) => ({
                            ...prevState,
                            [waveform]: false,
                        }));
                    }).catch((ex: any) => {
                        console.log("Failed to get waveforms...");
                        console.log(ex);
                        setWaveformsData((prevState: any) => ({
                            ...prevState,
                            [waveform]: [],
                        }));
                        setIsLoadingWaveforms((prevState: any) => ({
                            ...prevState,
                            [waveform]: false,
                        }));
                    });
                }
            }
        }
    }

    function select(uRanger: uPlot, newLft: any, newWid: any) {
        console.log("select called")
        lftWid.left = newLft;
        lftWid.width = newWid;
        // @ts-ignore
        uRanger.setSelect(lftWid, false);
    }

    function update(uRanger: uPlot, newLft: any, newWid: any) {
        let newRgt = newLft + newWid;
        let maxRgt = uRanger.bbox.width / devicePixelRatio;

        if (patientVisit && newWid > 0) {
            // This function only applies if there is a highlighted box already

            if (newLft >= 0 && newRgt <= maxRgt) {
                select(uRanger, newLft, newWid);
                let minMax = {
                    min: uRanger.posToVal(newLft, 'x'),
                    max: uRanger.posToVal(newLft + newWid, 'x'),
                }
                // @ts-ignore
                globalThis.zoomedRange = minMax;
                console.log("Setting width: update", plotWidth);
                setNumericOpts((prevNumerics: any) => {
                    const numerics = getAvailableNumerics(patientVisit, OPS_NUMERICS_FILTER);
                    return numerics.map((numeric: any, idx: number) => getNumericOpts(plotWidthRef, numeric, idx, handleShowTimelineContextMenu, null, minMax, updateWaveformPreview, patientVisit,
                        isLastVisibleNumericsPlot(numerics, numeric, numericsHiddenRef)));
                });
            }
        }
    }

    function bindMove(uRanger: uPlot, e: any, onMove: any) {
        x0 = e.clientX;
        lft0 = uRanger.select.left;
        wid0 = uRanger.select.width;

        const _onMove = debounce(onMove);
        on("mousemove", doc, _onMove);

        const _onUp = (e: any) => {
            off("mouseup", doc, _onUp);
            off("mousemove", doc, _onMove);
            // viaGrip = false;
        };
        on("mouseup", doc, _onUp);

        e.stopPropagation();
    }

    const updateWaveformPreview = (targetDate: Date) => {
        if (patientVisitRef.current) {
            fetchWaveforms(patientVisitRef.current, targetDate);
        }
    }

    /**
     * Adds the vertical line to the numeric plot, to indicate that there is an event
     * @param x 
     * @param patientVisit 
     * @param xAnnotationMarks 
     * @param markType 
     * @param markLabel 
     * @param lengthSeconds 
     */
    const addAnnotationToPlots = (x: any, patientVisit: any, markType: string, markLabel: string, lengthSeconds: number = 60) => {

        console.log("addAnnotationToPlots", markType, x, markLabel, lengthSeconds);

        let preExistingMarks;
        if (markType === "waveformMark") {
            // Note: There can only ever be one mark for waveformMark
            preExistingMarks = globalThis.xAnnotationMarksForNumerics.filter((mark: any) => mark.type !== markType);
        } else {
            preExistingMarks = globalThis.xAnnotationMarksForNumerics.filter((mark: any) => (mark.label !== markLabel || mark.from !== x));
        }

        // const truncatedMarkLabel = markLabel.split(/\(|\s\d/)[0];
        const truncatedMarkLabel = markLabel;
        
        const newXAnnotationMarks = [
            ...preExistingMarks,
            {
                type: markType,
                from: x,
                to: x + lengthSeconds,
                label: truncatedMarkLabel,
            },
        ];
        globalThis.xAnnotationMarksForNumerics = newXAnnotationMarks;
        if (markType === "waveformMark") {
            const newXAnnotationMarksForWaveforms = [
                {
                    type: markType,
                    from: x,
                    to: x + lengthSeconds,
                    label: markLabel,
                },
            ];
            globalThis.xAnnotationMarksForWaveforms = newXAnnotationMarksForWaveforms;
        }
        setWaveformAvailabilityOpts(getWaveformAvailabilityOpts(addAnnotationToPlots, handleShowTimelineContextMenu, plotWidth, patientVisit));

        console.log("Setting width: addAnnotationToPlot", plotWidth);
        // Set timeout to ensure that the zoom is retained
        setNumericOpts((prevNumerics: uPlot.Options[]) => {
            const numerics = getAvailableNumerics(patientVisit, OPS_NUMERICS_FILTER);

            return numerics.map(function (numeric: any, idx: number) {
                let minMax = getCurrMinMax(patientVisit, props.tenant);
                return getNumericOpts(plotWidthRef, numeric, idx, handleShowTimelineContextMenu, null, minMax, updateWaveformPreview, patientVisit,
                    isLastVisibleNumericsPlot(numerics, numeric, numericsHiddenRef))
            });
        });

        if (markType === "eventMark") {
            setHighlightedEvents((currHighlightedEvents: any[]) => [
                ...currHighlightedEvents.filter((mark: any) => (mark.name !== markLabel || mark.x !== x)),
                {
                    x: x,
                    name: truncatedMarkLabel,
                }
            ]);
        }

        if (markType === "waveformMark") {
            updateWaveformPreview(new Date(x * 1000));
        }
    }

    const handleShowLabsModal = (show: boolean, highlightedNumericTime?: string) => {
        setShowLabsModal(show);
        if (highlightedNumericTime) {
            setLabsModalHighlightedTime(highlightedNumericTime)
        } else {
            setLabsModalHighlightedTime(null);
        }
    }

    const handleShowOtherAnnotation = (highlightedNumericTime: number) => {
        let found = false;
        const localSelected: any = [];
        for (let annotation of otherAnnotations) {
            if (getMinuteTruncatedEpoch(annotation.annotation_time) === highlightedNumericTime) {
                found = true;
                localSelected.push({
                    ...annotation
                });
            }
        }
        if (found) {
            setSelectedOtherAnnotations(localSelected);
            setShowOtherAnnotationsModal(true);
        }
    }

    const handleShowTimelineContextMenu = (e: any, show: boolean, highlightedNumericTime?: string) => {
        e.preventDefault();
        setShowContextMenu(show);
        setContextMenuPos({ x: e.clientX, y: e.clientY });
        if (highlightedNumericTime) {
            setUserAnnotationHighlightedTime(highlightedNumericTime)
        } else {
            setUserAnnotationHighlightedTime(null);
        }
    }

    const handleRemoveHighlightedEvent = (name?: string, x?: number) => {
        console.log("handleRemoveHighlightedEvent", name, x)

        let preExistingMarks: any[] = [];
        if (name) {
            // User wishes to remove a specific highlighted event
            setHighlightedEvents((currHighlightedEvents: any[]) => [
                ...currHighlightedEvents.filter((mark: any) => (mark.name !== name || mark.x !== x)),
            ]);

            preExistingMarks = globalThis.xAnnotationMarksForNumerics.filter((mark: any) => (mark.label !== name || mark.from !== x));
        } else {
            // User wishes to remove all highlighted events (except for waveforms)
            setHighlightedEvents((currHighlightedEvents: any[]) => []);

            preExistingMarks = globalThis.xAnnotationMarksForNumerics.filter((mark: any) => {
                return (mark.type === "waveformMark")
            });
        }
        const newXAnnotationMarks = [
            ...preExistingMarks,
        ];

        globalThis.xAnnotationMarksForNumerics = newXAnnotationMarks;
        setWaveformAvailabilityOpts(getWaveformAvailabilityOpts(addAnnotationToPlots, handleShowTimelineContextMenu, plotWidth, patientVisit));

        console.log("Setting width: handleRemoveHighlightedEvent", plotWidth);
        setNumericOpts((prevNumerics: uPlot.Options[]) => {
            const numerics = getAvailableNumerics(patientVisit!, OPS_NUMERICS_FILTER);
            return numerics.map(function (numeric: any, idx: number) {
                let minMax = getCurrMinMax(patientVisit!, props.tenant);
                return getNumericOpts(plotWidthRef, numeric, idx, handleShowTimelineContextMenu, null, minMax, updateWaveformPreview, patientVisit,
                    isLastVisibleNumericsPlot(numerics, numeric, numericsHiddenRef))
            });
        });
    }

    function resetZoom(currPatientVisit?: PatientVisit, lockTimelineZoomOverride?: boolean) {
        console.log("Reset zoom outer...")

        let pt = currPatientVisit ? currPatientVisit : patientVisit;
        if (pt) {
            console.log("Reset zoom...")

            // Clear the zoomed range, which is a global variable that is set whenever there 
            // is an active annotation. This is required as adding annotations will inherently
            // cause the plot to redraw, which will reset the zoom.
            // @ts-ignore
            globalThis.zoomedRange = {};
            setSelectedTimelineMinMax({});
            let minMax = {
                min: getEarliestTime(pt) / 1000,
                max: getLatestTime(pt, props.tenant) / 1000,
            }

            console.log("Setting timeline width: chartsAndAnnotations", timelineOpts.width);
            setTimelineOpts(getTimelineOpts(addAnnotationToPlots, handleShowLabsModal, setShowUserAnnotationModal, handleShowOtherAnnotation, handleShowTimelineContextMenu, timelineOpts.width, timelinePlotContainerHeight, pt, setSelectedTimelineMinMax, lockTimelineZoomOverride !== undefined ? lockTimelineZoomOverride : lockTimelineZoom, props.tenant));

            console.log("Setting width: resetZoom", plotWidth);
            setNumericOpts(function (prevNumerics: any) {
                const numerics = getAvailableNumerics(pt!, OPS_NUMERICS_FILTER);
                return numerics.map((numeric: any, idx: number) => getNumericOpts(plotWidthRef, numeric, idx, handleShowTimelineContextMenu, timelineOpts.width, minMax, updateWaveformPreview, pt,
                    isLastVisibleNumericsPlot(numerics, numeric, numericsHiddenRef)))
            }.bind({ pt: pt }));
        }
    }

    const areAllNumericsEmpty = () => {
        let allNumericsEmpty = true;
        if (patientVisit) {
            getAvailableNumerics(patientVisit, OPS_NUMERICS_FILTER).forEach((numeric: string, idx: number) => {
                if (numericsData[numeric] && numericsData[numeric][0].length > 0) {
                    allNumericsEmpty = false;
                }
            });
        }
        return allNumericsEmpty;
    }

    const handleNumericsHiddenChange = (hiddenNumerics: any) => {
        setNumericsHidden(hiddenNumerics);
    }

    const handleTimelineResizeDrag = (e: any) => {
        e.preventDefault();
        const ref = timelinePlotContainerRef.current;
        const mminStep = 50;

        const onMouseMove = (e: any) => {
            document.body.style.cursor = "pointer";

            let newHeight = e.pageY - ref.getBoundingClientRect().top;

            if (newHeight >= mminStep) {
                setTimelinePlotContainerHeight(newHeight);
                console.log("Setting timeline width: handleTimelineResizeDrag", plotWidth);
                setTimelineOpts(getTimelineOpts(addAnnotationToPlots, handleShowLabsModal, setShowUserAnnotationModal, handleShowOtherAnnotation, handleShowTimelineContextMenu, plotWidth, newHeight, patientVisit!, setSelectedTimelineMinMax, lockTimelineZoom, props.tenant));
            }
        };

        const onMouseUp = (e: any) => {
            window.removeEventListener("mousemove", onMouseMove);
            document.body.style.cursor = "auto";

            if (e && e.target && e.target.className === "plot-resize") {
                let newHeight = e.pageY - ref.getBoundingClientRect().top;
                setTimeout(() => {
                    setOverviewData(verticallySeparatePoints(overviewData, newHeight));
                }, 1000);
            }
        };

        if (timelinePlotContainerRef) {
            window.addEventListener("mousemove", onMouseMove);
            window.addEventListener("mouseup", onMouseUp);
        }
    };

    const dispatchOnElement = (elementId: string, event: any) => {
        let overlays = document.getElementById(elementId)?.getElementsByClassName("u-over");
        if (overlays && overlays.length > 0) {
            if (overlays.item && overlays.item(0) !== null) {
                overlays.item(0)!.dispatchEvent(event);
            }
        }
    }

    const doubleClickOnTimeline = () => {
        var event = new MouseEvent('dblclick', {
            'view': window,
            'bubbles': true,
            'cancelable': true
        });
        dispatchOnElement("timeline-container", event);
        dispatchOnElement("waveform-container", event);
    }

    const [filterMapHighlightShow, setFilterMapHighlightShow] = useState<boolean>(false);
    const [filterOptions, setFilterOptions]: any = useState<any>({
        mapHighlight: 0,
        dimension: 0,
        waveforms: PREFERRED_WAVEFORMS_ORDER,
    });

    const [modelPredictions, setModelPredictions] = useState<any[]>([]);

    useEffect(() => {
        if (filterOptions["mapHighlight"] === 2) {
            callQuery({
                operation: ADMIT_RISK_SCORES_FOR_ACTIVE_PATIENTS,
            })
            .then((modelPredictions: any[]) => {
                setModelPredictions(modelPredictions);
            });
        }
    }, [filterOptions]);

    const updateFilterOptions = (option: string, val: any) => {
        setFilterOptions({
            ...filterOptions,
            [option]: val,
        });
    }

    const refresh = (updatedMetricNames: string[]) => {
        console.log("Refreshing metrics")
        callQuery({
            operation: METRICS_TRENDS,
            period: "1h",
            // start: getSqlDateStrFromDateStr(filterDateOptions.absoluteStart),
            // end: getSqlDateStrFromDateStr(filterDateOptions.absoluteEnd),
            start: getSqlDateStrFromDateStr(new Date(filterDateOptions.absoluteStart.getTime() - 1000 * 60 * 60 * 24 * 7)),
            end: getSqlDateStrFromDateStr(filterDateOptions.absoluteStart),
            dimension_name: "ALL",
            metric_names: updatedMetricNames,
            filter_dimension_name: "ALL",
            filter_dimension_values: selectedZone === "ALL" ? [] : [selectedZone], // TODO: Add hallway
        })
        .then((metrics: any[]) => {
            let data: any = {};
            let opts: any = {};
            let uniqueDimensionValues = new Set<string>();
            for (let metric of metrics) {
                uniqueDimensionValues.add(getMappedDimensionValue(filterOptions["dimension"], metric[1]));
            }
            let dimensionValues = Array.from(uniqueDimensionValues);
            let dimensionValueToIdx: any = {};
            dimensionValues.forEach((val: string, idx: number) => {
                // dimensionValueToIdx[val] = idx + 1; // To account for the offset of the time field for uPlot only
                dimensionValueToIdx[val] = idx; // To account for the offset of the time field
            })
            setDimensionValues(dimensionValues);

            let preData: any = {};
            let uniqueTimestamps: any = new Set();
            for (let metricName of updatedMetricNames) {
                preData[metricName] = {};
                for (let val of dimensionValues) {
                    preData[metricName][val] = {};
                }
            }
            for (let metric of metrics) {
                const metricName = metric[0];
                const dimensionName = getMappedDimensionValue(filterOptions["dimension"], metric[1]);
                if (isMetricCount(metricName)) {
                    // These are counts so we don't do any unit processes
                    preData[metricName][dimensionName][metric[2]] = metric[3];
                } else {
                    // Display in hours
                    preData[metricName][dimensionName][metric[2]] = parseFloat(metric[3]) / 3600;
                }
                uniqueTimestamps.add(metric[2]);
            }

            const sortedUniqueTimestamps: any = Array.from(uniqueTimestamps).sort();

            //
            // Format for Chart.js
            //
            for (let metricName of updatedMetricNames) {
                data[metricName] = {
                    datasets: []
                };
                dimensionValues.forEach((val: string, idx: number) => {
                    data[metricName].datasets.push({
                        data: [],
                        label: val,
                        borderColor: metricColors("Zone", idx, val),
                        backgroundColor: metricColors("Zone", idx, val),
                        hoverBorderColor: "blue",
                        hoverBackgroundColor: "blue",
                        fill: isMetricCount(metricName),
                        spanGaps: 1000 * 60 * 60 * 1,
                        borderWidth: 1,
                        hoverBorderWidth: 2,
                        pointRadius: 1,
                        pointHoverRadius: 2,
                    });
                });
                opts[metricName] = getMetricsLineOpts(isMetricCount(metricName), new Date(filterDateOptions.absoluteStart.getTime() - 1000 * 60 * 60 * 24 * 7), filterDateOptions.absoluteStart, chartRef, hoverPosition, setHoverPosition, setConfirmationTime, setShowConfirmationModal);
            }
            for (let ts of sortedUniqueTimestamps) {
                for (let metricName of updatedMetricNames) {
                    for (let val of dimensionValues) {
                        if (preData[metricName][val][ts]) {
                            data[metricName].datasets[dimensionValueToIdx[val]].data.push({
                                x: new Date(ts),
                                y: preData[metricName][val][ts]
                            })
                        }
                    }
                }
            }

            setMetricNameToData(data);
            setMetricNameToOps(opts);
        })
        .finally(() => {
            setIsLoadingMetrics(false);
        });
    }


    const getLatestMetricValue = (metricName: string, isY: boolean) => {
        if (metricNameToData && metricNameToData[metricName]) {
            const datasets = metricNameToData[metricName].datasets;
            if (datasets.length > 0) {
                const data = datasets[0].data;
                if (data.length > 0) {
                    if (isY) {
                        return data[data.length - 1].y;
                    } else {
                        return data[data.length - 1].x;
                    }
                }
            }
        }
        return null;
    }


    return (
        <>
            <Container ref={target} className="ops-container" fluid style={{ position: 'absolute', top: isDemoUser(props.user) ? '136px' : '64px', bottom: '0px', left: '0px', right: '0px' }}>
                {
                    isLoading ? (
                        <Row style={{textAlign: 'center'}}>
                            <Col>
                                <FontAwesomeIcon icon={faSpinner} spin />{' '}Loading...
                            </Col>
                        </Row>
                    ) : (
                        <>
                            <Row style={{marginBottom: '8px'}}>
                                <Col md={12}>
                                    <ButtonGroup size="sm" style={{marginRight: '16px'}}>
                                        <Button variant={selectedMapOrTable === "Map" ? "secondary" : "outline-secondary"} onClick={() => setSelectedMapOrTable("Map")}>Map</Button>
                                        <Button variant={selectedMapOrTable === "Table" ? "secondary" : "outline-secondary"} onClick={() => setSelectedMapOrTable("Table")}>Trackboard</Button>
                                    </ButtonGroup>
                                    <ButtonGroup size="sm" style={{marginRight: '16px'}}>
                                        <Button variant={selectedZone === "ALL" ? "secondary" : "outline-secondary"} onClick={() => setSelectedZone("ALL")}>All</Button>
                                        <Button variant={selectedZone === "ALPHA" ? "secondary" : "outline-secondary"} onClick={() => setSelectedZone("ALPHA")}>Alpha</Button>
                                        <Button variant={selectedZone === "BRAVO" ? "secondary" : "outline-secondary"} onClick={() => setSelectedZone("BRAVO")}>Bravo</Button>
                                        <Button variant={selectedZone === "CARDINAL" ? "secondary" : "outline-secondary"} onClick={() => setSelectedZone("CARDINAL")}>Cardinal</Button>
                                        <Button variant={selectedZone === "DELTA" ? "secondary" : "outline-secondary"} onClick={() => setSelectedZone("DELTA")}>Delta</Button>
                                        <Button variant={selectedZone === "EMERALD" ? "secondary" : "outline-secondary"} onClick={() => setSelectedZone("EMERALD")}>Emerald</Button>
                                        <Button variant={selectedZone === "FOXTROT" ? "secondary" : "outline-secondary"} onClick={() => setSelectedZone("FOXTROT")}>Foxtrot</Button>
                                    </ButtonGroup>
                                    <div style={{display: 'inline-block', position: 'relative'}}>
                                        <Button variant="secondary" size="sm" onClick={() => setFilterMapHighlightShow(true)}>
                                            {HIGHLIGHT_MAP_OPTIONS[filterOptions["mapHighlight"]].name} <FontAwesomeIcon icon={faCaretDown} />
                                        </Button>
                                        {filterMapHighlightShow && (
                                            <SingleSelectMenu
                                                selectedItem={filterOptions["mapHighlight"]}
                                                option="mapHighlight"
                                                updateFilterOptions={updateFilterOptions}
                                                availableItems={HIGHLIGHT_MAP_OPTIONS}
                                                setShow={setFilterMapHighlightShow}
                                            />
                                        )}
                                    </div>

                                    <div style={{display: 'inline-block', position: 'relative', float: 'right'}}>
                                        <ButtonGroup size="sm" style={{marginRight: '16px'}}>
                                            <Button
                                                size="sm" 
                                                variant={"secondary"}
                                                onClick={() => {
                                                    setFilterDateSelectorShow(true)
                                                }}
                                                disabled={false}
                                            >
                                                {`${formatUserReadableDate(filterDateOptions.absoluteStart, false)}`} <FontAwesomeIcon className="ml-1" icon={faCalendarDays} />
                                            </Button>
                                        </ButtonGroup>
                                        {filterDateSelectorShow && (
                                            <CustomDateSelector
                                                setIsHistoricalView={setIsHistoricalView}
                                                setShow={setFilterDateSelectorShow}
                                                filterDateOptions={filterDateOptions}
                                                setFilterDateOptions={setFilterDateOptions}
                                            />
                                        )}
                                    </div>
                                </Col>
                            </Row>
                            {
                                (filterDateOptions["absoluteStart"] > new Date("2010-03-01T00:00:00.000Z") && filterDateOptions["absoluteStart"] < new Date("2010-03-02T00:00:00.000Z")) ? (
                                    // Demo patient
                                    <></>
                                ) : filterDateOptions["absoluteStart"] < new Date("2020-08-20T00:00:00.000Z") || (filterDateOptions["absoluteStart"] > new Date("2024-01-31T00:00:00.000Z") && filterDateOptions["absoluteStart"] < new Date("2024-05-15T00:00:00.000Z")) ? (
                                    <Row>
                                        <Col md={12}>
                                            <Alert variant='danger' dismissible onClose={() => {setIsHistoricalView(false)}}>
                                                <strong>Point-in-Time Not Available</strong> Due to data limitations, point-in-time is only available between Aug 2020 to Jan 2024 or after 15 May 2024.
                                            </Alert>
                                        </Col>
                                    </Row>
                                ) : isHistoricalView ? (
                                    <Row>
                                        <Col md={12}>
                                            <Alert variant='success' dismissible onClose={() => {setIsHistoricalView(false)}}>
                                                <strong>Historical View</strong> You are seeing what the department was like on {getLocalDateStrFromDate(filterDateOptions["absoluteStart"])} {getLocalTimeStrFromDate(filterDateOptions["absoluteStart"])} ({getRelativeDate(filterDateOptions["absoluteStart"])}).
                                            </Alert>
                                        </Col>
                                    </Row>
                                ) : (
                                    <></>
                                )
                            }
                            <Row style={{position: 'fixed', top: isDemoUser(props.user) ? (isHistoricalView ? '236px' : '176px') : (isHistoricalView ? '166px' : '106px'), bottom: '0px', left: '0px', right: '0px'}}>
                                <Col style={{height: '100%', borderRight: '1px solid #DDD', overflowY: 'scroll'}} md={4}>
                                    {
                                        selectedMapOrTable === "Map" ? (
                                            <MapContainer attributionControl={false} crs={CRS.Simple}>
                                                <RasterMap
                                                    // @ts-ignore
                                                    img={zoneToImageSize.get(selectedZone)}
                                                    url={`/tiles/tiles-${selectedZone.toLowerCase()}/{z}/{x}/{y}.png`}
                                                    patients={allPatients}
                                                    selectedZone={selectedZone}
                                                    modelPredictions={modelPredictions}
                                                    mapHighlightType={HIGHLIGHT_MAP_OPTIONS[filterOptions["mapHighlight"]].id}
                                                    setInternalCSN={setInternalCSN}
                                                    setSelectedRoom={setSelectedRoom}
                                                />
                                            </MapContainer>
                                        ) : (
                                            <Table striped bordered hover style={{margin: '0px 0px 0px 8px'}}>
                                                <thead>
                                                    <tr>
                                                        <th>CSN</th>
                                                        <th>Bed</th>
                                                        <th>Chief Complaint</th>
                                                        <th>Elapsed</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {
                                                        allPatients.filter((p) => {
                                                            if (selectedZone === "ALL") {
                                                                return true;
                                                            } else if (p.bed && selectedZone.charAt(0) === p.bed[0]) {
                                                                return true;
                                                            } else {
                                                                return false;
                                                            }
                                                        }).map((patient) => (
                                                            <tr>
                                                                <td><a href={`/dashboard/${patient.internal_csn}`} target={isDemoUser(props.user) ? "" : "_blank"}><PersonalIdentifier identifier={patient.csn} /></a></td>
                                                                <td>{patient.bed}</td>
                                                                <td>{patient.chief_complaint}</td>
                                                                <td>{getHourMinuteDifference(new Date(patient.arrival_time), patient.departure_time ? new Date(patient.departure_time) : filterDateOptions.absoluteStart)}</td>
                                                            </tr>
                                                        ))
                                                    }
                                                </tbody>
                                            </Table>
                                        )
                                    }
                                </Col>
                                <Col md={4} style={{height: '100%', overflowY: 'scroll'}}>
                                {(true || selectedMode === "Patient") && (
                                    <>
                                    {
                                        patientVisit && internalCSN ? (
                                            <>
                                                <Row className="mb-2" style={{marginRight: '0px', borderTop: '1px dashed #CCC', paddingTop: '6px'}}>
                                                    <Col>
                                                        <Row xs={1} md={1} className="g-2 mb-2">
                                                            <Col style={{fontSize: '1rem'}}>
                                                                <span style={{lineHeight: '30px'}}>
                                                                    <strong>CSN </strong> <PersonalIdentifier identifier={patientVisit.csn} />
                                                                </span>
                                                            {
                                                                internalCSN && (
                                                                    <>
                                                                        <Button variant="text" style={{float: 'right'}} size="sm" onClick={() => {setPatientVisit(undefined)}}><FontAwesomeIcon icon={faTimes} /></Button>
                                                                        <Button variant="text" style={{float: 'right'}} size="sm" href={`/dashboard/${internalCSN}`} target={isDemoUser(props.user) ? "" : "_blank"}><FontAwesomeIcon icon={faArrowUpRightFromSquare} /></Button>
                                                                    </>
                                                                )
                                                            }
                                                            </Col>
                                                        </Row>
                                                        <Row xs={1} md={1} className="g-2 mb-2">
                                                            <Col style={{fontSize: '0.8rem'}}>
                                                                <Card>
                                                                    <Card.Body>
                                                                    {
                                                                        patientVisit && (
                                                                            <>
                                                                                <strong>MRN </strong> <PersonalIdentifier identifier={patientVisit.mrn} /> &bull; <strong>Age</strong> {patientVisit.age} &bull; <strong>Sex</strong> {patientVisit.gender} &bull; <strong>Arrival</strong> {getSecondDateStrFromDateStr(patientVisit.arrival_time)} &bull; <strong>CC</strong> {patientVisit.chief_complaint}
                                                                            </>
                                                                        )
                                                                    }
                                                                    </Card.Body>
                                                                </Card>
                                                            </Col>
                                                        </Row>

                                                        <Insights patientVisit={patientVisit} />
                                                        {/* <Row xs={1} md={1} className="g-2 mb-2">
                                                            <Col style={{fontSize: '0.8rem'}}>
                                                                <Accordion defaultActiveKey="0" flush>
                                                                    <Accordion.Item eventKey="0">
                                                                        <Accordion.Header>Key Insights (3)</Accordion.Header>
                                                                        <Accordion.Body>
                                                                            <Alert variant="danger" onClose={() => {}} dismissible>
                                                                                <div>
                                                                                    <strong>Heart rate decreasing due to amiodarone administration. </strong>
                                                                                </div>
                                                                                <div>
                                                                                    Heart rate averaged around 120 in the first three hours of visit but is now showing decrease after amiodarone administration at 13:42.
                                                                                </div>
                                                                            </Alert>
                                                                            <Alert variant="info" onClose={() => {}} dismissible>
                                                                                <div>
                                                                                    <strong>Heart rate decreasing due to amiodarone administration. </strong>
                                                                                </div>
                                                                                <div>
                                                                                    Heart rate averaged around 120 in the first three hours of visit but is now showing decrease after amiodarone administration at 13:42.
                                                                                </div>
                                                                            </Alert>
                                                                        </Accordion.Body>
                                                                    </Accordion.Item>
                                                                </Accordion>
                                                            </Col>
                                                        </Row> */}

                                                        <Row xs={1} md={1} className="g-2 mb-2">
                                                            <Col>
                                                                <Card>
                                                                    <Card.Body id="timeline-container" ref={timelinePlotContainerRef} style={{ paddingBottom: '0px', paddingLeft: '12px', paddingTop: '24px', height: `${timelinePlotContainerHeight}px` }}>
                                                                        <div className="text-right" style={{ position: 'absolute', right: '16px', top: '4px', width: '620px' }}>
                                                                            <div className="text-right float-end plot-indicator-container">
                                                                                <div className='plot-indicator-container-item'>
                                                                                    <span className="plot-indicator-container-icon indicator-normal" style={{ fontSize: '1rem' }}>&#9650;</span>{' '}<span className="plot-indicator-container-desc">Lab Collection</span>
                                                                                </div>
            
                                                                                <div className='plot-indicator-container-item'>
                                                                                    <span className="plot-indicator-container-icon indicator-normal"><FontAwesomeIcon icon={faCircle} /></span>{' '}<span className="plot-indicator-container-desc">Medication Administration</span>
                                                                                </div>
            
                                                                                <div className='plot-indicator-container-item'>
                                                                                    <span className="plot-indicator-container-icon indicator-report"><FontAwesomeIcon icon={faCircle} /></span>{' '}
                                                                                    <OverlayTrigger
                                                                                        placement="left"
                                                                                        overlay={
                                                                                            <Tooltip>
                                                                                                Includes radiology imaging reports and other text documents
                                                                                            </Tooltip>
                                                                                        }
                                                                                    >
                                                                                        <span className="plot-indicator-container-desc">Reports</span>
                                                                                    </OverlayTrigger>
                                                                                </div>
            
                                                                                <div className='plot-indicator-container-item'>
                                                                                    <span className="plot-indicator-container-icon indicator-user"><FontAwesomeIcon icon={faCircle} /></span>{' '}
                                                                                    <OverlayTrigger
                                                                                        placement="left"
                                                                                        overlay={
                                                                                            <Tooltip>
                                                                                                Right-click on timeline to add annotation
                                                                                            </Tooltip>
                                                                                        }
                                                                                    >
                                                                                        <span className="plot-indicator-container-desc">User Annotation</span>
                                                                                    </OverlayTrigger>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                        {
                                                                            isLoadingOverview ? (
                                                                                <>
                                                                                    <p style={{ textAlign: 'center', marginTop: '48px', marginBottom: '50px' }}>
                                                                                        <FontAwesomeIcon icon={faSpinner} spin /> Loading...
                                                                                    </p>
                                                                                </>
                                                                            ) : timelineOpts && overviewData[0] && overviewData[0].length >= 0 ? (
                                                                                <>
                                                                                    <UplotReact
                                                                                        // @ts-ignore
                                                                                        options={timelineOpts}
                                                                                        // TODO: Use meds, labs, etc.
                                                                                        data={overviewData as uPlot.AlignedData}
                                                                                        onCreate={(uRanger: uPlot) => {
                                                                                            timelinePlotRef.current = uRanger;
                                                                                            const sel = uRanger.root.querySelector(".u-over");
                                                                                            on("dblclick", sel, (e: any) => {
                                                                                                // This ensures that we reset the scales of our chart after we have let the double click event propagate.
                                                                                                setTimeout(() => {
                                                                                                    resetZoom();
                                                                                                }, 0);
                                                                                            });
                                                                                            on("mousedown", sel, (e: any) => {
                                                                                                bindMove(uRanger, e, (e: any) => update(uRanger, lft0 + (e.clientX - x0), wid0));
                                                                                            });
                                                                                        }}
                                                                                        resetScales={false}
                                                                                    />
                                                                                </>
                                                                            ) : (
                                                                                <>
                                                                                    <p style={{ textAlign: 'center', marginTop: '48px', marginBottom: '50px' }}>
                                                                                        <FontAwesomeIcon icon={faExclamationCircle} /> No Medications and Labs Found
                                                                                    </p>
                                                                                </>
                                                                            )
                                                                        }
                                                                    </Card.Body>
            
                                                                    <div
                                                                        className="plot-resize"
                                                                        onMouseDown={(e) => handleTimelineResizeDrag(e)}
                                                                    >
                                                                    </div>
                                                                </Card>
                                                            </Col>
                                                        </Row>
            
                                                        {/* <DispoRisk tenant={props.tenant} showModal={showDispoRiskModal} showAssistant={showAssistant} featureImportance={featureImportance} timestamps={timestamps} patientVisit={patientVisit} setShowModal={setShowDispoRiskModal} dispoAdmitRiskTrend={dispoAdmitRiskTrend} plotWidthRef={plotWidthRef} /> */}

                                                        <Row xs={1} md={1} className="g-2" id="plot-container">
                                                            <Col>
                                                                <Card>
                                                                    <Card.Body style={{ paddingBottom: '0px' }}>
                                                                        {/* If we do not have a min height, every time the highlighted event is added it will push the plots below causing them to rerender */}
                                                                        <Row style={{ minHeight: '28px' }}>
                                                                            <Col md={9}>
                                                                                <span style={{ marginRight: '16px' }}>
                                                                                    <FontAwesomeIcon icon={faChartLine} /> <strong>Numerics</strong>
                                                                                </span>
                                                                                {
                                                                                    highlightedEvents.map((highlightedEvent: any) => (
                                                                                        <ButtonGroup key={highlightedEvent.name + highlightedEvent.id} style={{ marginRight: '4px' }}>
                                                                                            <Button variant="secondary" className="btn-xsm">{highlightedEvent.name}</Button>
                                                                                            <Button variant="secondary" className="btn-xsm" onClick={() => handleRemoveHighlightedEvent(highlightedEvent.name, highlightedEvent.x)}><FontAwesomeIcon icon={faClose} /></Button>
                                                                                        </ButtonGroup>
                                                                                    ))
                                                                                }
                                                                            </Col>
                                                                            <Col md={3}>
                                                                                <div className="text-right float-end plot-indicator-container">
                                                                                    <div className='plot-indicator-container-item'>
                                                                                        <span className="plot-indicator-container-icon indicator-normal"><FontAwesomeIcon icon={faCircle} /></span>{' '}<span className="plot-indicator-container-desc">Charted</span>
                                                                                    </div>
            
                                                                                    <div className='plot-indicator-container-item'>
                                                                                        <span className="plot-indicator-container-icon indicator-normal"><FontAwesomeIcon fontWeight={800} icon={faMinus} /></span>{' '}<span className="plot-indicator-container-desc">Monitor</span>
                                                                                    </div>
                                                                                </div>
                                                                            </Col>
                                                                        </Row>
                                                                        {
                                                                            !isLoadingNumerics && areAllNumericsEmpty() && (
                                                                                <>
                                                                                    <p style={{ textAlign: 'center', marginTop: '48px', fontSize: '64px', color: '#EEE' }}>
                                                                                        <FontAwesomeIcon icon={faRocket} />
                                                                                    </p>
                                                                                    <p style={{ textAlign: 'center', marginTop: '48px', marginBottom: '50px', color: '#EEE' }}>
                                                                                        No data found
                                                                                    </p>
                                                                                </>
                                                                            )
                                                                        }
                                                                        {patientVisit && getAvailableNumerics(patientVisit, OPS_NUMERICS_FILTER).map((numeric: string, idx: number) => (
                                                                            <div key={numeric}>
                                                                                {
                                                                                    isLoadingNumerics ? (
                                                                                        <>
                                                                                            <span style={{ fontWeight: 'bold', fontSize: '14px' }}>{numeric}</span>
                                                                                            <p style={{ textAlign: 'center', marginTop: '48px', marginBottom: '50px' }}>
                                                                                                <FontAwesomeIcon icon={faSpinner} spin /> Loading...
                                                                                            </p>
                                                                                        </>
                                                                                    ) : (numericsHidden[numeric] && numericsData[numeric] && numericsData[numeric][0].length > 0) ? (
                                                                                        <>
                                                                                            <div style={{ position: 'relative', height: '24px', marginTop: '12px' }}>
                                                                                                <div style={{ position: 'absolute', top: '-4px', width: '100%' }}>
                                                                                                    <hr />
                                                                                                </div>
                                                                                                <p style={{ backgroundColor: 'white', paddingRight: '12px', fontSize: '0.7rem', position: 'absolute', left: '0px', top: '2px', height: '12px' }}>
                                                                                                    <strong>{numeric}</strong>
                                                                                                </p>
                                                                                                <div style={{ position: 'absolute', left: '-12px', top: '0px', cursor: 'pointer' }}>
                                                                                                    <FontAwesomeIcon icon={faCaretRight} onClick={() => { handleNumericsHiddenChange({ ...numericsHidden, [numeric]: false }) }} />
                                                                                                </div>
                                                                                            </div>
                                                                                        </>
                                                                                    ) : numericOpts[idx] && numericsData[numeric] && numericsData[numeric][0].length > 0 ? (
                                                                                        <>
                                                                                            <div style={{ position: 'relative' }}>
                                                                                                <p style={{ backgroundColor: 'white', transform: 'translateY(50%) rotate(-90deg)', marginTop: '6px', marginBottom: '0px', transformOrigin: 'left center', fontSize: '0.7rem', position: 'absolute', left: '10px', top: isDemoUser(props.user) ? '64px' : '64px' }}>
                                                                                                    <strong>{numeric}</strong>
                                                                                                </p>
                                                                                                <div style={{ marginLeft: '16px' }}>
                                                                                                    <UplotReact
                                                                                                        options={numericOpts[idx]}
                                                                                                        data={numeric === "Temp" ? convertToTempUnit(numericsData[numeric]!, tempUnit) : (numericsData[numeric] as uPlot.AlignedData)}
                                                                                                        onCreate={(uRanger: uPlot) => {
                                                                                                            const sel = uRanger.root.querySelector(".u-over");
                                                                                                            on("dblclick", sel, (e: any) => {
                                                                                                                // This ensures that we reset the scales of our chart after we have let the double click event propagate.
                                                                                                                setTimeout(() => {
                                                                                                                    resetZoom();
                                                                                                                }, 0);
                                                                                                            });
                                                                                                        }}
                                                                                                        resetScales={false}
                                                                                                    />
                                                                                                </div>
                                                                                                {
                                                                                                    numeric === "Temp" && (
                                                                                                        <div style={{ position: 'absolute', left: '-7px', bottom: '-9px', cursor: 'pointer', fontSize: '0.8rem', backgroundColor: 'white', borderRadius: '4px', padding: '4px' }}>
                                                                                                            {
                                                                                                                tempUnit === "F" ? (
                                                                                                                    <><a onClick={() => {
                                                                                                                        setTempUnit('C');
                                                                                                                        setTimeout(() => {
                                                                                                                            resetZoom();
                                                                                                                        }, 0);
                                                                                                                    }}>C</a> | <a onClick={() => {
                                                                                                                        setTempUnit('F');
                                                                                                                        setTimeout(() => {
                                                                                                                            resetZoom();
                                                                                                                        }, 0);
                                                                                                                    }}><strong>F</strong></a></>
                                                                                                                ) : (
                                                                                                                    <><a onClick={() => {
                                                                                                                        setTempUnit('C');
                                                                                                                        setTimeout(() => {
                                                                                                                            resetZoom();
                                                                                                                        }, 0);
                                                                                                                    }}><strong>C</strong></a> | <a onClick={() => {
                                                                                                                        setTempUnit('F');
                                                                                                                        setTimeout(() => {
                                                                                                                            resetZoom();
                                                                                                                        }, 0);
                                                                                                                    }}>F</a></>
                                                                                                                )
                                                                                                            }
                                                                                                        </div>
                                                                                                    )
                                                                                                }
                                                                                                <div style={{ position: 'absolute', left: '-12px', top: isDemoUser(props.user) ? '64px' : '64px', cursor: 'pointer' }}>
                                                                                                    <FontAwesomeIcon icon={faCaretDown} onClick={() => { handleNumericsHiddenChange({ ...numericsHidden, [numeric]: true }) }} />
                                                                                                </div>
                                                                                                {
                                                                                                    numericsRefRangeData[numeric] && (
                                                                                                        <div style={{ position: 'absolute', right: '12px', top: '6px', cursor: 'pointer' }} className="u-mark-ref-range">
                                                                                                            {numericsRefRangeData[numeric]}
                                                                                                        </div>
                                                                                                    )
                                                                                                }
                                                                                            </div>
                                                                                        </>
                                                                                    ) : (
                                                                                        <></>
                                                                                    )
                                                                                }
                                                                            </div>
                                                                        ))}
                                                                    </Card.Body>
                                                                </Card>
                                                            </Col>
                                                        </Row>
                                                        <Row xs={1} md={1} className="g-2 mt-1">
                                                            <Col>
                                                                <Card>
                                                                    <Card.Body id="waveform-container" className="waveform-availability" style={{ paddingBottom: '0px' }}>
                                                                        <Row>
                                                                            <Col md={6}>
                                                                                <FontAwesomeIcon icon={faHeartPulse} /> <strong>Waveform Preview</strong>
                                                                            </Col>

                                                                            <Col md={6}>
                                                                                <div className="text-right float-end plot-indicator-container">
                                                                                    <div className='plot-indicator-container-item'>
                                                                                        <span className="plot-indicator-container-icon indicator-abnormal"><FontAwesomeIcon icon={faCircle} /></span>{' '}

                                                                                        <span className="plot-indicator-container-desc plot-indicator-container-desc-dropdown" onClick={() => setFilterWaveformAnnotationShow(true)}>
                                                                                            {uniqueECGClassifications.length > 0 ? uniqueECGClassifications[filterWaveformOptions["waveformAnnotation"]].name : ""}{' '}<FontAwesomeIcon icon={faCaretDown} />
                                                                                        </span>

                                                                                        {filterWaveformAnnotationShow && (
                                                                                            <WaveformAnnotationSingleSelectMenu
                                                                                                selectedItem={filterWaveformOptions["waveformAnnotation"]}
                                                                                                option="waveformAnnotation"
                                                                                                updateFilterOptions={updateFilterWaveformOptions}
                                                                                                availableItems={uniqueECGClassifications}
                                                                                                setShow={setFilterWaveformAnnotationShow}
                                                                                            />
                                                                                        )}
                                                                                    </div>

                                                                                    <div className='plot-indicator-container-item'>
                                                                                        {patientVisit && !patientVisit.archived && (
                                                                                            <>
                                                                                                <span className="plot-indicator-container-icon indicator-normal"><FontAwesomeIcon icon={faCircle} /></span>{' '}<span className="plot-indicator-container-desc">Sufficient Quality</span>{' '}
                                                                                            </>
                                                                                        )}
                                                                                        <span className={patientVisit && patientVisit.archived ? "plot-indicator-container-icon indicator-normal" : "plot-indicator-container-icon indicator-insufficient"}><FontAwesomeIcon icon={faCircle} /></span>{' '}<span className="plot-indicator-container-desc">{patientVisit && patientVisit.archived ? "Available" : "Insufficient Quality"}</span>
                                                                                    </div>
                                                                                </div>
                                                                            </Col>
                                                                        </Row>
                                                                        {
                                                                            isLoadingAnnotations ? (
                                                                                <>
                                                                                    <p style={{ textAlign: 'center', marginTop: '48px', marginBottom: '50px' }}>
                                                                                        <FontAwesomeIcon icon={faSpinner} spin /> Loading...
                                                                                    </p>
                                                                                </>
                                                                            ) : waveformAvailabilityOpts && waveformAvailabilityData[0].length > 0 ? (
                                                                                <>
                                                                                    <UplotReact
                                                                                        // @ts-ignore
                                                                                        options={waveformAvailabilityOpts}
                                                                                        // TODO: Use meds, labs, etc.
                                                                                        data={waveformAvailabilityData as uPlot.AlignedData}
                                                                                    />
                                                                                </>
                                                                            ) : (
                                                                                <>
                                                                                    <p className="not-found-text">
                                                                                        <FontAwesomeIcon icon={faExclamationCircle} /> Annotations not available.
                                                                                    </p>
                                                                                    <hr />
                                                                                </>
                                                                            )
                                                                        }
                                                                        {getAvailableWaveforms(patientVisit, filterOptions).map((waveform: string, idx: number) => (
                                                                            <div key={waveform}>
                                                                                {
                                                                                    isLoadingWaveforms[waveform] ? (
                                                                                        <>
                                                                                            <p style={{ textAlign: 'center', marginTop: '48px', marginBottom: '50px' }}>
                                                                                                <FontAwesomeIcon icon={faSpinner} spin /> Loading...
                                                                                            </p>
                                                                                        </>
                                                                                    ) : waveformOpts[idx] && waveformsData[waveform] && waveformsData[waveform][0] && waveformsData[waveform][0].length > 0 ? (
                                                                                        <>
                                                                                            <UplotReact
                                                                                                options={waveformOpts[idx]}
                                                                                                data={waveformsData[waveform] as uPlot.AlignedData}
                                                                                                onCreate={(uRanger: uPlot) => {
                                                                                                    //
                                                                                                }}
                                                                                                resetScales={false}
                                                                                            />
                                                                                        </>
                                                                                    ) : (
                                                                                        <>
                                                                                            <p className="not-found-text">
                                                                                                <FontAwesomeIcon icon={faExclamationCircle} /> <strong>{waveform}</strong> not available. Try selecting a different time period above.
                                                                                            </p>
                                                                                            <hr />
                                                                                        </>
                                                                                    )
                                                                                }
                                                                            </div>
                                                                        ))}
                                                                    </Card.Body>
                                                                </Card>
                                                            </Col>
                                                        </Row>
                                                    </Col>
                                                </Row>
                                                <Row style={{ marginBottom: '4px' }}>
                                                    {
                                                        patientVisit && (
                                                            <>
                                                                <LabsModal showModal={showLabsModal} setShowModal={handleShowLabsModal} labs={labsData} patientVisit={patientVisit} addAnnotationToPlots={addAnnotationToPlots} highlightedLabTime={labsModalHighlightedTime} />
                                                                <MedsModal showModal={showMedsModal} setShowModal={setShowMedsModal} meds={medsData} patientVisit={patientVisit} addAnnotationToPlots={addAnnotationToPlots} />
                                                                <OrdersModal showModal={showOrdersModal} setShowModal={setShowOrdersModal} patientVisit={patientVisit} addAnnotationToPlots={addAnnotationToPlots} />

                                                                <OtherAnnotationModal showModal={showOtherAnnotationsModal} setShowModal={setShowOtherAnnotationsModal} selectedAnnotations={selectedOtherAnnotations} patientVisit={patientVisit} addAnnotationToPlots={addAnnotationToPlots} />
                                                                <UserAnnotationModal show={showUserAnnotationModal} setShow={setShowUserAnnotationModal} patientVisit={patientVisit} userAnnotations={userAnnotations} setUserAnnotations={setUserAnnotations} userAnnotationHighlightedTime={userAnnotationHighlightedTime} fetchChartAndAnnotations={fetchChartAndAnnotations} />
                                                                <TimelineContextMenu show={showContextMenu} setShow={setShowContextMenu} position={contextMenuPos} setShowUserAnnotationModal={setShowUserAnnotationModal} resetZoom={doubleClickOnTimeline} />
                                                            </>
                                                        )
                                                    }
                                                </Row>
                                            </>
                                        ) : (selectedRoom && !internalCSN) ? (
                                            <>
                                                <p style={{ textAlign: 'center', marginTop: '20vh', fontSize: '64px', color: '#24b364' }}>
                                                    <FontAwesomeIcon icon={faCheckCircle} />
                                                </p>
                                                <p style={{ textAlign: 'center', marginTop: '48px', marginBottom: '50px', color: '#24b364' }}>
                                                    Room is available
                                                </p>
                                            </>
                                        ) : (
                                            <>
                                                <p style={{ textAlign: 'center', marginTop: '20vh', fontSize: '64px', color: '#CCC' }}>
                                                    <FontAwesomeIcon icon={faBedPulse} />
                                                </p>
                                                <p style={{ textAlign: 'center', marginTop: '48px', marginBottom: '50px', color: '#CCC' }}>
                                                    Select a room to see patient details
                                                </p>
                                            </>
                                        )
                                    }
                                    </>
                                )}
                                </Col>
                                <Col md={4} style={{height: '100%', overflowY: 'scroll'}}>
                                {
                                    (true || selectedMode === "Metrics") && (
                                        <Row className="mt-2 mb-2">
                                            <Col md={12} style={{color: '#999999', fontSize: '0.8rem'}}>
                                                Seven day window ending at {formatUserReadableDate(filterDateOptions.absoluteStart, false)}. <a href={`/metrics?start=${filterDateOptions.absoluteStart.getTime() - 1000 * 60 * 60 * 24 * 7}&end=${filterDateOptions.absoluteStart.getTime()}`} target={isDemoUser(props.user) ? "" : "_blank"}>See Full Metrics{' '}<FontAwesomeIcon icon={faArrowUpRightFromSquare} /></a>
                                            </Col>
                                        </Row>
                                    )
                                }
                                {
                                    (true || selectedMode === "Metrics") && metricNames.map(metricName => (
                                        <Row className="mt-2 mb-2">
                                            <Col md={12}>
                                                <Card>
                                                    <Card.Body>
                                                        <div style={{ position: 'absolute', left: '16px', top: '12px', width: '640px', fontSize: '0.9rem' }}>
                                                            <strong>{METRIC_NAME_TO_DISPLAY[metricName]}</strong>{' '}
                                                            <OverlayTrigger
                                                                placement="right"
                                                                overlay={
                                                                    <Tooltip>
                                                                        {METRIC_NAME_TO_DESCRIPTION[metricName]}
                                                                    </Tooltip>
                                                                }
                                                            >
                                                                <span><FontAwesomeIcon icon={faQuestionCircle} />{' '}</span>
                                                            </OverlayTrigger>

                                                            {
                                                                <span style={{color: '#999999', fontSize: '0.8rem'}}>
                                                                    latest value: <strong>{roundNumberIfAppropriate(getLatestMetricValue(metricName, true))}</strong> at {formatUserReadableDate(getLatestMetricValue(metricName, false))}
                                                                </span>
                                                            }
                                                        </div>
                                                        <div className="text-right" style={{ position: 'absolute', right: '16px', top: '10px', width: '820px' }}>
                                                            <div className="text-right float-end plot-indicator-container">
                                                                {
                                                                    dimensionValues.map((dimensionValue: string, idx: number) => (
                                                                        <div className='plot-indicator-container-item'>
                                                                            <span className="plot-indicator-container-icon" style={{ fontSize: '1rem', color: metricColors("Zone", idx, dimensionValue) }}><FontAwesomeIcon icon={faCircle} /></span>{' '}<span className="plot-indicator-container-desc">{dimensionValue}</span>
                                                                        </div>
                                                                    ))
                                                                }
                                                            </div>
                                                        </div>
                                                        <div style={{ marginTop: '24px' }}>
                                                            {
                                                                metricNameToData && metricNameToData[metricName] && metricNameToData[metricName].datasets.length > 0 && metricNameToOps && metricNameToOps[metricName] && (
                                                            
                                                                    <div>
                                                                        {
                                                                            // @ts-ignore
                                                                            <Line options={metricNameToOps[metricName]} data={metricNameToData[metricName]} height="240px" ref={chartRef} />
                                                                        }
                                                                    </div>
                                                                )
                                                            }
                                                        </div>
                                                    </Card.Body>
                                                </Card>
                                            </Col>
                                        </Row>
                                    ))
                                }
                                </Col>
                            </Row>
                        </>
                    )
                }
            </Container>
            <ConfirmationModal showConfirmationModal={showConfirmationModal} setShowConfirmationModal={setShowConfirmationModal} confirmTime={confirmationTime} />
        </>
    );
};
